import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, ArrowRight } from 'lucide-react';
import { apiClient } from '../../services/api/apiClient';
import { getImageUrl } from '../../utils/imageUtils';
import { useLenis } from '../../hooks/useLenis';
import toast from '../../utils/toast';

export const EmailVerificationPageTW: React.FC = () => {
  useLenis();

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState<string>('');

  const token = searchParams.get('token');
  const logoUrl = getImageUrl('/images/branding/renditefuchs-logo.svg');

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setStatus('error');
        setMessage('Ungültiger oder fehlender Verifikationstoken.');
        return;
      }

      try {
        const response = await apiClient.verifyEmail(token);

        if (response.success) {
          setStatus('success');
          setMessage(response.message || 'E-Mail-Adresse wurde erfolgreich bestätigt!');
          toast.success('E-Mail-Adresse erfolgreich bestätigt!');
        } else {
          setStatus('error');
          setMessage(response.error || 'Fehler bei der E-Mail-Bestätigung.');
          toast.error(response.error || 'Fehler bei der E-Mail-Bestätigung.');
        }
      } catch (error) {
        console.error('Email verification error:', error);
        setStatus('error');
        setMessage('Fehler bei der E-Mail-Bestätigung. Bitte versuchen Sie es erneut.');
        toast.error('Fehler bei der E-Mail-Bestätigung.');
      }
    };

    verifyEmail();
  }, [token]);

  const handleContinue = () => {
    if (status === 'success') {
      const redirect = searchParams.get('redirect');
      const loginPath = redirect
        ? `/login?redirect=${encodeURIComponent(redirect)}`
        : '/login';
      navigate(loginPath);
    } else {
      navigate('/');
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
      <div className="w-full max-w-sm space-y-10">
        {/* Logo & Header */}
        <div>
          <Link to="/" aria-label="Zur Startseite" className="block">
            <img
              alt="Renditefuchs"
              src={logoUrl}
              className="mx-auto h-10 w-auto"
            />
          </Link>
          <h2 className="mt-10 text-center text-2xl/9 font-bold tracking-tight text-gray-900 dark:text-white">
            {status === 'loading' && 'E-Mail wird bestätigt...'}
            {status === 'success' && 'Bestätigung erfolgreich'}
            {status === 'error' && 'Bestätigung fehlgeschlagen'}
          </h2>
        </div>

        <div className="space-y-6">
          {/* Loading */}
          {status === 'loading' && (
            <div className="flex flex-col items-center justify-center py-8">
              <Loader2 className="h-10 w-10 text-brand animate-spin mb-4" />
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Bitte warten Sie, während wir Ihre E-Mail-Adresse bestätigen.
              </p>
            </div>
          )}

          {/* Success */}
          {status === 'success' && (
            <>
              <div className="p-4 rounded-lg bg-status-success/10 border border-status-success/50">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-status-success shrink-0 mt-0.5" />
                  <p className="text-sm text-status-success">{message}</p>
                </div>
              </div>

              <button
                type="button"
                onClick={handleContinue}
                className="flex w-full justify-center items-center gap-2 rounded-md bg-brand px-3 py-2 text-sm/6 font-semibold text-gray-900 hover:bg-brand-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand transition-colors"
              >
                <span>Weiter zum Login</span>
                <ArrowRight className="h-4 w-4" />
              </button>
            </>
          )}

          {/* Error */}
          {status === 'error' && (
            <>
              <div className="p-4 rounded-lg bg-status-error/10 border border-status-error/50">
                <div className="flex items-start gap-3">
                  <XCircle className="h-5 w-5 text-status-error shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-status-error">{message}</p>
                    <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                      Falls das Problem weiterhin besteht, kontaktieren Sie bitte den Support oder fordern Sie eine neue Bestätigungs-E-Mail an.
                    </p>
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={handleContinue}
                className="flex w-full justify-center items-center gap-2 rounded-md bg-gray-100 dark:bg-gray-800 px-3 py-2 text-sm/6 font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              >
                Zur Startseite
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
